import { PlatformLocation } from '@angular/common';
import { Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  Filter,
  FilterOptions,
  Loan,
  OfferRatings,
  OffersOfApplication,
  Result,
} from '../type/result_type';
import { GuardService } from './guard.service';
import { ResultpageService } from './resultpage.service';
import { LoginService } from './login.service';
import { OfferDataUtilsService } from './offer-data-utils.service';
import { CacheService, CacheEntry } from './cache.service';
import { ChronicleService } from '../chronicle/chronicle.service';
declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class ResultpagesharedService {
  private resultCache = new Map<string, CacheEntry<Result>>();
  public remainingTime = signal<number>(0);
  public dataLoan!: Loan;
  public dataFilter!: Filter;
  public dataFilterOptions!: FilterOptions;
  public dataOffers!: OffersOfApplication[];
  public dataNoOffers!: OffersOfApplication[];
  public dataInClosingProcess!: OffersOfApplication[];
  public offersRatings!: OfferRatings;

  private subGet!: Subscription;
  private subTimer!: Subscription;
  private subFilterPost!: Subscription;
  private subLoanPost!: Subscription;
  private interval1!: Subscription;
  private subEternalGet!: Subscription;
  private filterPostGet!: Subscription;
  private localLoanGet!: Subscription;

  public showDefaultPlaceholders: boolean = true;
  public loading: boolean = true;
  public filterOverlay: boolean = false;
  public loanPostClick: boolean = false;
  private interval1Start: boolean = false;
  public resultPage: boolean = false;
  public eternalPage: boolean = false;
  private filterLoad: boolean = false;
  private subGetLoad: boolean = false;
  private subFilterPostLoad: boolean = false;
  private subTimerLoad: boolean = false;
  private newOffers: boolean = false;
  private localLoanLoad: boolean = false;
  public twoApplicants!: boolean;
  public allocationGroupId: string | null = null;
  public apptrackToken!: string;
  public loanRequestError!: boolean;
  public showDataOffers: boolean = true;
  public showDataInClosingProcess: boolean = true;
  public firstPost: boolean = false;
  public firstPostData: boolean = false;

  public time!: number;
  private token!: string;
  private crmToken!: string;
  public pathname!: string;
  private hashname!: string;
  public basePath!: string;
  private timeStamp!: string;
  public loanRequestErrorMessage!: string;

  private renderer: Renderer2;
  static readonly GOOD_CUSTOMER_ALLOCATION_GROUP_ID = 'AE77Y' as const;
  private thankYouDataLayerBasePushed = false;
  private thankYouDataLayerErfolgPushed = false;
  private allocationGroupReadyNotified = false;
  private readonly allocationGroupReadySubject = new Subject<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private resultpageService: ResultpageService,
    private location: PlatformLocation,
    private guardService: GuardService,
    private rendererFactory: RendererFactory2,
    private loginService: LoginService,
    private offerDataUtils: OfferDataUtilsService,
    private cacheService: CacheService,
    private chronicle: ChronicleService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  initComponent(options?: { eternalTab?: 'active' }): void {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    this.token = urlParams.get('t') ?? '';
    this.crmToken = urlParams.get('crmt') ?? '';

    this.pathname = this.location.pathname;
    this.hashname = this.location.hash;

    if (this.guardService.checkKreditcenter() === 'kreditcenterUser') {
      this.eternalPage = true;
      this.offerDataUtils.setKreditcenterTab(options?.eternalTab ?? 'angebote');
      this.loginService.checkLogin().subscribe({
        next: () => {
          this.getEternalResult();
        },
        error: () => {
          this.loading = false;
        },
      });
    } else if (this.guardService.checkKreditcenter() === 'crmToken') {
      this.eternalPage = true;
      this.getKreditcenterCrm(this.crmToken);
    } else {
      if (!this.token || this.token == 'undefined') {
        this.router.navigate(['/', 'test']);
      } else {
        this.resultPage = true;
        this.getResult(this.token);
      }
    }

    this.syncThankYouDataLayer();
  }

  private syncThankYouDataLayer(): void {
    window['dataLayer'] = window['dataLayer'] || [];
    const deviceOutput = window['wf_deviceoutput'].toLowerCase();
    const withCpid = (payload: Record<string, unknown>) => ({
      ...payload,
      ...(payload['cpid'] != null && payload['cpid'] !== ''
        ? {}
        : { cpid: window['wf_cpid'] }),
    });

    if (!this.thankYouDataLayerBasePushed) {
      this.thankYouDataLayerBasePushed = true;
      window['dataLayer'].push(
        withCpid({
          ...window['dataLayer'][0],
          event: 'dankeseite',
          deviceoutput: deviceOutput,
        }),
      );
    }
    if (
      !this.thankYouDataLayerErfolgPushed &&
      this.allocationGroupId !== null
    ) {
      this.thankYouDataLayerErfolgPushed = true;
      window['dataLayer'].push(
        withCpid({
          areaid: 'kredit-resultpage-thankyou',
          event: 'dankeseite_erfolg',
          success: this.matchesGoodCustomerAllocationGroup() ? 'Yes' : 'No',
          deviceoutput: deviceOutput,
        }),
      );
    }
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this.unsubscribeAll();

    if (this.filterLoad) {
      this.filterPostGet.unsubscribe();
    }
    if (this.localLoanLoad) {
      this.localLoanGet.unsubscribe();
    }
  }

  public getObserv$!: Observable<any>;
  private postFilterObserv$!: Observable<any>;
  private postLoanObserv$!: Observable<any>;

  // Delegate to OfferDataUtilsService
  get showMobInterestExplanation() {
    return this.offerDataUtils.showMobInterestExplanation;
  }
  set showMobInterestExplanation(value: any) {
    this.offerDataUtils.showMobInterestExplanation = value;
  }

  get eternalOffersDataObserv$() {
    return this.offerDataUtils.eternalOffersDataObserv$;
  }

  updateOffersData(data: Result): void {
    this.offerDataUtils.updateEternalOffersData(data);
  }

  matchesGoodCustomerAllocationGroup(
    allocationGroupId?: string | null,
  ): boolean {
    const id =
      allocationGroupId === undefined
        ? this.allocationGroupId
        : allocationGroupId;
    return id === ResultpagesharedService.GOOD_CUSTOMER_ALLOCATION_GROUP_ID;
  }

  onAllocationGroupReady(): Observable<string> {
    if (this.allocationGroupReadyNotified && this.allocationGroupId !== null) {
      return of(this.allocationGroupId);
    }
    return this.allocationGroupReadySubject.pipe(take(1));
  }

  private notifyAllocationGroupReady(): void {
    if (!this.allocationGroupReadyNotified && this.allocationGroupId !== null) {
      this.allocationGroupReadyNotified = true;
      this.allocationGroupReadySubject.next(this.allocationGroupId);
    }
  }

  private getCacheKey(): string {
    return 'resultpage';
  }

  private getEternalCacheKey(): string {
    const tab = this.offerDataUtils.getKreditcenterTab();
    return `eternal:${tab}`;
  }

  invalidateCache(): void {
    this.cacheService.clear(this.resultCache);
  }

  invalidateEternalCache(): void {
    this.cacheService.delete(this.resultCache, this.getEternalCacheKey());
  }

  //---get data for kredit result page---
  getResult(token: string): void {
    this.getObserv$ = this.route.paramMap.pipe(
      switchMap(() => {
        const cacheKey = this.getCacheKey();

        // Return cached data if valid, avoiding redundant API call
        if (this.cacheService.isValid(this.resultCache, cacheKey)) {
          return of(this.cacheService.get(this.resultCache, cacheKey)!);
        }

        // Fetch from API and cache the response
        return this.resultpageService.getResult(token).pipe(
          tap((data: Result) => {
            this.cacheService.set(this.resultCache, cacheKey, data);
          }),
        );
      }),
    );

    this.subGet = this.getObserv$.subscribe((data: Result) => {
      this.remainingTime.set(data.context.tokenTimeRemaining);
      this.allocationGroupId = data.allocationGroupId ?? null;
      this.notifyAllocationGroupReady();
      this.syncThankYouDataLayer();
      this.dataFilter = data.filter;
      this.dataOffers = data.offersOfApplication;
      this.dataNoOffers = this.offerDataUtils.sortNoOffersData(
        data.noOffersOfApplication,
      );
      this.offersRatings = data.offersRatings;
      this.twoApplicants = data.context.twoApplicants;
      this.apptrackToken = data.context.apptrackToken;
      this.resultpageService.updateStateLoanData(data.loan);
      this.resultpageService.updatedFilerDataPost(data.filter);
      this.resultpageService.updateFilerOptionsData(data.filterOptions);
      this.resultpageService.updatedStateFilerData(1);
      this.resultpageService.updateOffersData(this.dataOffers);
      this.resultpageService.updateSmilyContext(data.context?.smily);
      if (this.dataOffers && this.dataOffers.length >= 3) {
        this.showDefaultPlaceholders = false;
      }
      void this.chronicle.startComparison(data);
      this.timeTracker();
      this.subGetLoad = true;
    });
  }
  //---get data for kredit result page---

  //---calculate time of requests for kredit result page---
  timeTracker(): void {
    const stopTime = 120;
    const pollInterval = 5000; // 5 seconds
    let source;

    //---timer for count seconds--
    source = timer(0, 1000);
    //---timer for count seconds--

    this.subTimer = source.subscribe((val) => {
      this.subTimerLoad = true;
      this.time = val;

      if (val === 0) {
        // Start polling every 5 seconds
        this.interval1 = timer(0, pollInterval)
          .pipe(
            map(() => {
              this.interval1Start = true;
              this.firstPost = this.time === 0;
              return this.postFilter();
            }),
          )
          .subscribe();
      }

      //---stop send requests after 2 minutes---
      if (val === stopTime) {
        this.interval1?.unsubscribe();
        this.subTimer?.unsubscribe();
        this.loading = false;
        this.showDefaultPlaceholders = false;
        this.newOffers = false;
      }
    });
  }
  //---calculate time of requests for kredit result page---

  //---post data for kredit result page---
  postFilter(): void {
    this.loading = true;
    let obj: any = {};

    this.filterPostGet = this.resultpageService.filterDataPostObserv$.subscribe(
      (data) => {
        this.filterLoad = true;

        if (!this.newOffers) {
          // Scenario 1: normal filter change — strip newComparison
          obj = { ...data };
          delete obj.newComparison;
        } else {
          this.localLoanGet =
            this.resultpageService.loanDataPostObserv$.subscribe((dataLoan) => {
              this.localLoanLoad = true;
              obj = { ...data };
              if (
                obj.creditDuration ||
                obj.loanAmount ||
                obj.creditProtection
              ) {
                // User explicitly changed loan-related filters — filter takes priority
                delete obj.newComparison;
              } else {
                // No conflicting filter values — new comparison takes priority
                obj['newComparison'] = dataLoan;
                obj['newComparison']['comparisonTimestamp'] = this.timeStamp;
              }
            });
        }
      },
    );

    this.postFilterObserv$ = this.route.paramMap.pipe(
      switchMap(() => {
        if (this.resultPage) {
          return this.resultpageService.postFilter(this.token, obj);
        }
        if (this.eternalPage) {
          return this.resultpageService.postEternalFilter(obj);
        }
      }),
    );

    this.subFilterPost = this.postFilterObserv$
      .pipe(
        tap((data: Result) => {
          const cacheKey = this.getCacheKey();
          this.cacheService.set(this.resultCache, cacheKey, data);
        }),
      )
      .subscribe((data) => {
        this.dataFilter = data.filter;
        this.dataFilterOptions = data.filterOptions;
        this.dataOffers = data.offersOfApplication;
        this.dataNoOffers = this.offerDataUtils.sortNoOffersData(
          data.noOffersOfApplication,
        );
        this.offersRatings = data.offersRatings;
        this.resultpageService.updateFilerOptionsData(data.filterOptions);
        this.resultpageService.updateOffersData(this.dataOffers);
        this.resultpageService.updateSmilyContext(data.context?.smily);

        if (data.allocationGroupId != null && this.allocationGroupId == null) {
          this.allocationGroupId = data.allocationGroupId;
          this.notifyAllocationGroupReady();
          this.syncThankYouDataLayer();
        }

        //---stop polling when backend signals completion---
        if (data.continuePolling === false) {
          this.interval1?.unsubscribe();
          this.subTimer?.unsubscribe();
          this.loading = false;
          this.showDefaultPlaceholders = false;
          this.newOffers = false;
        }

        //---stop polling after 60s if no offers found---
        if (this.time > 60 && this.dataOffers.length === 0) {
          this.interval1?.unsubscribe();
          this.loading = false;
          this.newOffers = false;
          this.unsubscribeAll();
        }
        if (this.dataOffers && this.dataOffers.length >= 3) {
          this.showDefaultPlaceholders = false;
        }
        if (this.resultPage && this.time > 180) {
          this.loading = false;
        }
        if (this.eternalPage) {
          this.loading = false;
        }
        //---remove overlay from filter and chips after data coming---
        this.filterOverlay = false;
        this.resultpageService.updatedOverlay(this.filterOverlay);
        //---remove overlay from filter and chips after data coming---
        this.subFilterPostLoad = true;

        this.firstPostData = true;
      });
  }
  //---post data for kredit result page---

  //---post data for kredit result page---
  postLoan(): void {
    this.loading = true;
    let date = new Date();
    this.timeStamp = date.toISOString();

    this.postLoanObserv$ = this.route.paramMap.pipe(
      distinctUntilChanged(),
      switchMap(() => {
        return this.resultpageService.postLoan(this.token);
      }),
    );
    this.subLoanPost = this.postLoanObserv$
      .pipe(
        catchError((err) => {
          if (err.status === 409) {
            this.loanRequestError = true;
            this.loanRequestErrorMessage = err.error;
          } else if (err.status === 406) {
            this.loanRequestError = true;
            this.loanRequestErrorMessage = err.error;
          }
          throw 'catch error ' + err;
        }),
      )
      .subscribe({
        next: () => {
          this.loanRequestError = false;
          this.loanPostClick = true;
          this.unsubscribeAll();
          this.loading = false;

          this.resultpageService.updateFilerOptionsData(1);
          this.resultpageService.updateOffersData([]);
          // on recalculation of loan set no offer sentence hide.
          this.showDefaultPlaceholders = true;
          this.newOffers = true;

          // Preserve current sorting while clearing all other filter selections,
          this.resultpageService.filterDataPostObserv$
            .pipe(take(1))
            .subscribe((currentFilter) => {
              const sorting =
                currentFilter && currentFilter !== 1
                  ? currentFilter.sorting
                  : undefined;
              this.resultpageService.updatedFilerDataPost(
                sorting ? { sorting } : {},
              );
            });
          // Reset filter state so checkboxes under 'Kredite filtern' are unchecked.
          this.resultpageService.updatedStateFilerData([]);

          this.timeTracker();
        },
        error: () => {
          this.loading = false;
        },
      });
  }
  //---post data for kredit result page---

  //---load library for user login---

  //---data for kreditcenter page---
  dataKreditcenter(data) {
    if (data && Object.keys(data).length !== 0) {
      this.dataFilter = data.filter;
      this.dataOffers = data.offersOfApplication;
      this.dataInClosingProcess = data.offersOfApplicationInClosingProcess;
      if (this.dataOffers && this.dataOffers.length === 0) {
        this.showDataOffers = false;
      }
      if (this.dataInClosingProcess && this.dataInClosingProcess.length === 0) {
        this.showDataInClosingProcess = false;
      }
      this.offersRatings = data?.offersRatings || [];

      this.resultpageService.updatedFilerDataPost(data.filter);
      this.resultpageService.updateFilerOptionsData(data.filterOptions);
      this.resultpageService.updatedStateFilerData(1);
      this.resultpageService.updateOffersData(this.dataOffers);
      this.resultpageService.updateSmilyContext(data.context?.smily);
      if (this.dataOffers && this.dataOffers.length >= 3) {
        this.showDefaultPlaceholders = false;
      }
      this.loading = false;
    }
  }
  //---data for kreditcenter page---

  //---get data for kreditcenter page---
  getEternalResult(): void {
    const cacheKey = this.getEternalCacheKey();

    // Use cached data if valid
    let request$: Observable<any>;
    if (this.cacheService.isValid(this.resultCache, cacheKey)) {
      request$ = of(this.cacheService.get(this.resultCache, cacheKey)!);
    } else {
      // Fetch from API and cache the response
      request$ =
        this.offerDataUtils.getKreditcenterTab() === 'active'
          ? this.resultpageService.getActiveData()
          : this.resultpageService.getKreditcenterData();

      request$ = request$.pipe(
        tap((data: Result) => {
          this.cacheService.set(this.resultCache, cacheKey, data);
        }),
      );
    }

    this.subEternalGet = request$.pipe(take(1)).subscribe((data: any) => {
      this.dataKreditcenter(data);
      this.updateOffersData(data);
    });
  }
  //---get data for kreditcenter page---

  //---get data for kreditcenter crm page---
  getKreditcenterCrm(token: string): void {
    this.subEternalGet = this.resultpageService
      .getKreditcenterCrm(token)
      .pipe(take(1))
      .subscribe((data: any) => {
        this.dataKreditcenter(data);
      });
  }
  //---get data for kreditcenter crm page---

  unsubscribeAll(): void {
    if (this.subGetLoad) {
      this.subGet?.unsubscribe();
    }
    if (this.subTimerLoad) {
      this.subTimer?.unsubscribe();
    }
    if (this.subFilterPostLoad) {
      this.subFilterPost?.unsubscribe();
    }
    if (
      this.loanPostClick &&
      this.hashname == '#/' &&
      !this.guardService.checkKreditcenter()
    ) {
      this.subLoanPost?.unsubscribe();
    }
    if (
      this.interval1Start &&
      this.hashname == '#/' &&
      !this.guardService.checkKreditcenter()
    ) {
      this.interval1?.unsubscribe();
    }
  }

  getSingleOffer(offerId: string): OffersOfApplication | undefined {
    return this.offerDataUtils.getSingleOfferById(this.dataOffers, offerId);
  }

  // show rating only when available
  hasCustomerRatings(productId: string): boolean {
    if (!productId || !this.offersRatings?.ratings) {
      return false;
    }

    const rating = this.offersRatings.ratings.find(
      (r) => r.productId === productId,
    );

    return rating ? rating.totalRaters > 0 : false;
  }

  // read averageRate and totalRaters from offersRatings with matched productId
  getCustomerRatingsData(
    productId: string,
  ): { averageRate: number; totalRaters: number } | null {
    if (!productId || !this.offersRatings?.ratings) {
      return null;
    }

    const rating = this.offersRatings.ratings.find(
      (r) => r.productId === productId,
    );

    if (rating && rating.totalRaters > 0) {
      return {
        averageRate: rating.averageRate,
        totalRaters: rating.totalRaters,
      };
    }

    return null;
  }

  // get full rating data based on productId for customer rating component.
  getCustomerRatingByProductId(productId: string) {
    if (!productId || !this.offersRatings?.ratings) {
      return null;
    }

    return (
      this.offersRatings.ratings.find((r) => r.productId === productId) || null
    );
  }
}
